import { useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'

import { DocumentPreviewShell } from './document-preview-shell'

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`

interface PDFPreviewProps {
  maxPages?: number
  src: string
  title?: string
}

export const PDFPreview = ({ src, maxPages, title = 'PDF document' }: PDFPreviewProps): React.ReactElement => {
  const [numPages, setNumPages] = useState(0)
  const [pageWidth, setPageWidth] = useState<number>(208)
  const previewContainerRef = useRef<HTMLDivElement>(null)

  function onDocumentLoadSuccess({ numPages: loadedPageCount }: { numPages: number }) {
    setNumPages(loadedPageCount)
  }

  useEffect(() => {
    const updatePageWidth = () => {
      const containerWidth = previewContainerRef.current?.clientWidth ?? 0
      if (!containerWidth) return
      setPageWidth(Math.max(Math.floor(containerWidth - 24), 208))
    }

    updatePageWidth()

    const resizeObserver = new ResizeObserver(updatePageWidth)
    if (previewContainerRef.current) {
      resizeObserver.observe(previewContainerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return (
    <DocumentPreviewShell
      href={src}
      mimeType="application/pdf"
      title={title}
      footerLabel="Download PDF"
      bodyClassName="bg-gray-100 dark:bg-muted/30"
    >
      <div ref={previewContainerRef} className="flex max-h-[420px] w-full flex-col items-center overflow-auto">
        <Document file={src} onLoadSuccess={onDocumentLoadSuccess}>
          <div className="flex w-full flex-col items-center gap-3">
            {Array.from({ length: maxPages ? Math.min(numPages, maxPages) : numPages }, (_, index) => (
              <div
                key={`page_${index + 1}`}
                className="w-full max-w-full overflow-hidden rounded-md bg-white shadow-[0_8px_16px_-16px_rgba(15,23,42,0.35)]"
              >
                <Page pageNumber={index + 1} renderAnnotationLayer={false} renderTextLayer={false} width={pageWidth} />
              </div>
            ))}
          </div>
        </Document>
      </div>
    </DocumentPreviewShell>
  )
}
